<template>
	<div class="relative flex items-center" :class="rootClass">
		<button
			v-if="isAuthenticated && showPickerToggle"
			ref="toggleRef"
			type="button"
			data-shopping-list-toggle
			class="w-7 sm:w-10 h-7 sm:h-10 flex-none flex items-center justify-center rounded-full transition-colors duration-300 focus-outline hover:bg-background-noticeable"
			:class="{'bg-background-noticeable': isShoppingListOpen}"
			aria-label="Zobrazit možnosti"
			@click="toggleOpen"
		>
			<NuxtIcon
				name="more-vertical"
				class="text-icon-primary text-base sm:text-xl transition-transform duration-300"
				:class="isShoppingListOpen ? 'rotate-90' : 'rotate-0'"
			/>
		</button>
		<div :class="stepperWrapperClass">
			<Transition
				:mode="transitionMode"
				:enter-active-class="transitionEnterActive"
				:leave-active-class="transitionLeaveActive"
				:enter-from-class="transitionEnterFrom"
				:leave-to-class="transitionLeaveTo"
			>
				<div
					v-if="isExpanded && !isTextItem"
					key="stepper"
					:class="stepperClass"
				>
					<PlusMinus
						#default="{value}"
						:model-value="stepperAmount"
						:max="99"
						:disabled="isLocked"
						removable
						remove-icon-class="[&_svg]:!text-icon-tertiary"
						:label-left="stepperAmount <= 1 ? 'Odstranit ze seznamu' : 'Odebrat'"
						label-right="Přidat"
						:button-size-class="isAuthenticated ? 'h-6 sm:h-8' : 'h-[1.875rem] sm:h-[2.375rem]'"
						@update:model-value="handleUpdate"
						@remove="onRemove"
					>
						<span class="text-center rem:min-w-[40px]">{{ value }}</span>
					</PlusMinus>
				</div>
				<button
					v-else
					key="icon"
					type="button"
					class="flex items-center justify-center gap-0.5 sm:gap-1 rounded-full border border-border-secondary shadow-green-sm transition-colors duration-150 focus-outline disabled:opacity-60 hover:bg-background-noticeable"
					:class="[
						iconButtonClass,
						isOnList ? 'bg-background-noticeable' : 'bg-white',
						isAuthenticated ? 'h-8 sm:h-10 px-[0.44rem] sm:px-[0.563rem]' : 'h-9 sm:h-11 px-[0.563rem] sm:px-[0.688rem]',
					]"
					:disabled="isLocked"
					:aria-label="isOnList ? 'Odebrat ze seznamu' : 'Přidat na seznam'"
					@click="onIconClick"
				>
					<span v-if="isOnList" data-count class="typo-body-m text-body-primary">
						{{ amount }}
					</span>
					<NuxtIcon name="custom/shopping-list-add" class="text-icon-primary text-base sm:text-xl" />
				</button>
			</Transition>
		</div>
		<ProductShoppingListPickerPanel
			v-if="embeddedPicker && showPickerToggle"
			:visible="!!isShoppingListOpen"
			:reference="toggleRef"
			@close="isShoppingListOpen = false"
		/>
	</div>
</template>

<script setup lang="ts">
	import type {Gsoa_ProductCatalog_ProductDetail as ProductDetail} from '~/openapi';
	import type {ShoppingListAddMethod} from '~/utils/tracking';

	interface Props {
		product?: ProductDetail;
		disabled?: boolean;
		embeddedPicker?: boolean;
		showPickerToggle?: boolean;
		/** On mobile, keep the +/- stepper in normal flow instead of overlaying the add button. */
		inlineMobileStepper?: boolean;
		/** GTM `shopping_list_add_item` method (source of the add interaction). */
		method?: ShoppingListAddMethod;
	}

	const props = withDefaults(defineProps<Props>(), {
		disabled: false,
		embeddedPicker: true,
		showPickerToggle: true,
		inlineMobileStepper: false,
		method: 'product_cart',
	});
	const isShoppingListOpen = defineModel<boolean>('isShoppingListOpen');

	const {isAuthenticated} = useAuth();
	const toggleRef = useTemplateRef('toggleRef');
	const isMobileViewport = useTailwindBreakpoints().smaller('sm');

	const useInlineMobileStepper = computed(() => props.inlineMobileStepper && isMobileViewport.value);

	const rootClass = computed(() => (useInlineMobileStepper.value ? 'w-auto' : undefined));

	const stepperWrapperClass = computed(() => (
		useInlineMobileStepper.value
			? 'shrink-0'
			: 'max-sm:relative max-sm:h-8 max-sm:shrink-0 w-full'
	));

	const iconButtonClass = computed(() => (useInlineMobileStepper.value ? undefined : 'w-full'));

	const stepperClass = computed(() => (
		useInlineMobileStepper.value
			? ''
			: 'max-sm:absolute max-sm:right-0 max-sm:top-1/2 max-sm:-translate-y-1/2 max-sm:z-20'
	));

	const transitionMode = computed(() => (
		useInlineMobileStepper.value || !isMobileViewport.value ? 'out-in' : undefined
	));

	const transitionEnterActive = computed(() => (
		useInlineMobileStepper.value
			? 'transition duration-300 origin-center'
			: isMobileViewport.value
				? 'transition-opacity duration-200 ease-out'
				: 'transition duration-300 origin-center'
	));
	const transitionLeaveActive = computed(() => (
		useInlineMobileStepper.value
			? 'transition duration-150 origin-center [&_[data-count]]:opacity-0'
			: isMobileViewport.value
				? 'transition-opacity duration-150 ease-in [&_[data-count]]:opacity-0'
				: 'transition duration-150 origin-center [&_[data-count]]:opacity-0'
	));
	const transitionEnterFrom = computed(() => (
		useInlineMobileStepper.value || !isMobileViewport.value ? 'opacity-0 scale-x-75' : 'opacity-0'
	));
	const transitionLeaveTo = computed(() => (
		useInlineMobileStepper.value || !isMobileViewport.value ? 'opacity-0 scale-x-75' : 'opacity-0'
	));

	const isExpanded = ref(false);

	const {
		amount,
		add,
		isTextItem,
		stepperAmount,
		isLocked,
		onUpdate,
		onRemove: removeFromList,
	} = useProductShoppingListStepper({
		product: () => props.product,
		disabled: () => props.disabled,
		method: props.method,
		onListChange: (value) => {
			if (value === 0) {
				stopCollapse();
				isExpanded.value = false;
			}
		},
	});

	const isOnList = useHydrationSafeListMembership(amount);

	const {start: startCollapse, stop: stopCollapse} = useTimeoutFn(() => {
		isExpanded.value = false;
	}, 3500, {immediate: false});

	function keepOpen() {
		stopCollapse();
		startCollapse();
	}

	async function onIconClick() {
		if (isLocked.value) {
			return;
		}
		if (isTextItem.value) {
			if (isOnList.value) {
				await onRemove();
			} else {
				await add();
			}
			return;
		}
		if (amount.value === 0) {
			await add();
		}
		isExpanded.value = true;
		keepOpen();
	}

	function handleUpdate(value: number) {
		onUpdate(value);
		keepOpen();
	}

	async function onRemove() {
		await removeFromList(() => {
			stopCollapse();
			isExpanded.value = false;
		});
	}

	function toggleOpen() {
		isShoppingListOpen.value = !isShoppingListOpen.value;
	}

	watch(amount, (value) => {
		if (value === 0) {
			isExpanded.value = false;
		}
	});
</script>
